// Helpers for turning a user prompt into a report build and harvesting the
// resulting chat into report config. Colocated with the Reports views that
// consume them.

import { WIDGET_KEY } from '@/utils/hooks/reports';
import {
    canonicalExcludeTerms,
    hasVisualFor,
} from '@/ui/components/AiAssistant/visuals';
import { matchWidgetType } from './widgetCatalog';

/**
 * Phrase the composer's prompt as an explicit report request. This is sent as
 * an ordinary panel-chat message — visible in the conversation and saved to
 * history — so it must read like something the user would say; the system
 * prompt's Reports section supplies all the research/tool guidance.
 */
export const buildReportPrompt = (userPrompt) =>
    `Create a report: ${userPrompt}`;

/**
 * Wrap a free-text request for the single-widget "add widget" flow. Unlike
 * buildReportPrompt (which biases toward many tool calls to fill a whole report),
 * this steers the assistant to ONE focused get_entity_data widget answering the
 * request — harvestWidgets then appends that widget to the existing report.
 */
export const buildAddWidgetPrompt = (userPrompt) =>
    `You are Spate's expert trend researcher adding ONE widget to an existing ` +
    `report. Make exactly the get_entity_data tool call needed to answer the ` +
    `user's request (every entity_data tool output produces a visual). If an ` +
    `entity is named, resolve it first, then make a SINGLE focused data call — ` +
    `do not build a full report or chain extra calls. ` +
    `User request '${userPrompt}'`;

/**
 * Merge the `_entity_map` payloads from every get_entity call into one
 * `{ [index]: { id, route } }` map. The agent assigns entity indices globally
 * across the chat and rides this map to the client as a top-level `_`-prefixed
 * key on each get_entity result, so it lets us turn a get_entity_data call's
 * transient entity_index into a stable entity reference.
 */
const buildEntityIndexMap = (messages) => {
    const map = {};
    for (const message of messages) {
        if (message.role !== 'assistant') continue;
        for (const part of message.parts ?? []) {
            if (part.type === 'tool-get_entity' && part.output?._entity_map) {
                Object.assign(map, part.output._entity_map);
            }
        }
    }
    return map;
};

const canonicalHashtag = (name) => {
    const value = name.trim().toLowerCase();
    return value.startsWith('#') ? value : `#${value}`;
};

/**
 * Turn a finished build chat into a list of re-runnable widget configs: one per
 * completed get_entity_data call that a registry widget can render. Each widget
 * stores only the resolved tool kwargs (no data) — the report fetches fresh data
 * per widget on view.
 */
export const harvestWidgets = (messages) => {
    const entityMap = buildEntityIndexMap(messages);
    return messages
        .filter((message) => message.role === 'assistant')
        .flatMap((message) => message.parts ?? [])
        .filter(
            (part) =>
                part.type === 'tool-get_entity_data' &&
                part.state === 'output-available' &&
                part.output?.success,
        )
        .map((part) => {
            // Carry the tool call's args verbatim so widgets stay in lock-step
            // with get_entity_data — minus the entity indices, which are
            // transient to the chat and resolved here to the stable ids the
            // call executes with.
            const {
                entity_index: entityIndex,
                related_entity_index: relatedEntityIndex,
                entity_indices: entityIndices,
                ...args
            } = part.input ?? {};
            const resolved = entityMap[String(entityIndex)];
            if (resolved) {
                args.entity_id = resolved.id;
                if (resolved.route) args.entity_route = resolved.route;
                // The map's type is ground truth (it came from the get_entity
                // hit); the model regularly omits entity_type when it holds an
                // index, and a widget whose args carry an id without its type
                // fails to execute. Maps from older chats lack the type.
                if (resolved.type) args.entity_type = resolved.type;
            }
            const resolvedRelated = entityMap[String(relatedEntityIndex)];
            if (resolvedRelated) {
                args.related_entity_id = resolvedRelated.id;
            }
            // A pinned list (entity_indices) drops indices not in the map so
            // one bad pin doesn't break the whole widget.
            let pinned = [];
            if (Array.isArray(entityIndices)) {
                pinned = entityIndices
                    .map((index) => entityMap[String(index)]?.id)
                    .filter(Boolean);
            } else if (Array.isArray(args.entity_ids)) {
                pinned = args.entity_ids.filter(Boolean);
            }
            delete args.entity_ids;
            if (pinned.length) {
                args.entity_ids = [...new Set(pinned)].sort();
            } else if (matchWidgetType(args)?.entity === 'none') {
                return null;
            }
            if (Array.isArray(args.hashtags)) {
                const names = args.hashtags
                    .filter(Boolean)
                    .map(canonicalHashtag);
                delete args.hashtags;
                if (names.length) args.hashtags = [...new Set(names)].sort();
            }
            // Dropped before the shape is checked: a non-array survives the
            // backend (Python reads {} as absent) and throws on Edit.
            const rawExclude = args.exclude;
            delete args.exclude;
            if (Array.isArray(rawExclude)) {
                const terms = canonicalExcludeTerms(rawExclude.filter(Boolean));
                if (terms.length) args.exclude = terms;
            }
            if (!hasVisualFor(args)) return null;
            return { id: part.toolCallId, tool: 'get_entity_data', args };
        })
        .filter(Boolean);
};

/**
 * Seed React Query's widget cache from a finished chat's get_entity_data
 * outputs, keyed exactly as the report view queries (`[WIDGET_KEY, widget.args]`).
 * The view still background-refetches, but renders immediately instead of
 * flashing loaders. Shared by the report builder and the chat→report CTA.
 */
export const warmWidgetCache = (queryClient, widgets, messages) => {
    const outputByCallId = {};
    for (const message of messages) {
        for (const part of message.parts ?? []) {
            if (
                part.type === 'tool-get_entity_data' &&
                part.state === 'output-available'
            ) {
                outputByCallId[part.toolCallId] = part.output;
            }
        }
    }
    for (const widget of widgets) {
        const output = outputByCallId[widget.id];
        if (output) queryClient.setQueryData([WIDGET_KEY, widget.args], output);
    }
};
